import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

/**
 * One row of os_campaign_funnel (supabase/migrations/20260808010000_os_campaign_funnel.sql).
 *
 * total_leads/pending_drafts/sent_count are PER-CAMPAIGN real (reused straight from
 * os_campaign_stats). Every `_key`-suffixed field is a LAW 5 key-level AGGREGATE: computed
 * per campaign_key (crm_lead_inbox.campaign_key / crm_outreach.source_campaign_key) and
 * surfaced identically on every heyreach_campaign_id row that shares that key — never an
 * apportionment across the campaigns sharing a key, because the underlying data carries no
 * per-campaign split to apportion.
 *
 * The `_key` fields are `null` when campaign_key itself is null (no marketing lane data is
 * knowable for this campaign at all) and a real number — including 0 — once campaign_key is
 * set. Do not collapse those two cases in the UI: null means "connect a campaign_key", 0 means
 * "connected, and genuinely no activity yet in this lane".
 *
 * There is no harvest lane and no replied lane here — both fact-closed out in the migration
 * header (U1: mktg_shadow_outputs has zero authenticated RLS policies, so it would read a
 * permanent fake zero, not merely a currently-empty one; U2: crm_outreach has no reply
 * marker anywhere in its schema). There is no credits lane either — v1 renders that as a
 * hardcoded "—" in the UI with a code comment, not a join that can never mean anything.
 */
export interface CampaignFunnelRow {
  organization_id: string;
  heyreach_campaign_id: string;
  campaign_key: string | null;
  total_leads: number;
  pending_drafts: number;
  sent_count: number;
  inbox_pending_key: number | null;
  inbox_approved_key: number | null;
  outreach_connection_sent_key: number | null;
  outreach_connected_key: number | null;
  outreach_declined_key: number | null;
}

// ─────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────

// os_campaign_funnel is security_invoker OVER os_campaign_stats, which is itself
// security_invoker over os_linkedin_drafts — security_invoker composes through nested views,
// so this row is still USER-SEAT scoped (migration 015: a `member` sees only their own
// drafts' totals), not merely organization-scoped. Same S9a/useCampaigns.ts lesson
// (campaignDetailQueryKey): the authenticated user id must be part of the cache key, or two
// members of the same organization can read/overwrite each other's cached rows.
export const campaignFunnelQueryKey = (
  userId: string | null,
  organizationId: string | null,
  campaignId: string | undefined,
): readonly ['campaign-funnel', string | null, string | null, string | undefined] =>
  ['campaign-funnel', userId, organizationId, campaignId];

export const useCampaignFunnel = (
  campaignId: string | undefined,
): UseQueryResult<CampaignFunnelRow | null> => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<CampaignFunnelRow | null>({
    queryKey: campaignFunnelQueryKey(userId, organizationId, campaignId),
    queryFn: async () => {
      if (!campaignId || !organizationId) return null;

      const { data, error } = await supabase
        .from('os_campaign_funnel')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('heyreach_campaign_id', campaignId)
        .maybeSingle();

      if (error) throw error;
      return (data as CampaignFunnelRow | null) ?? null;
    },
    enabled: !!campaignId && userId !== null && organizationId !== null,
    staleTime: 30000,
  });
};
